import React, { useEffect, useState } from "react";
import Button from "../../../components/FormComponents/Button";
import Input from "../../../components/FormComponents/Input";
import Label from "../../../components/FormComponents/Label";
import Textarea from "../../../components/FormComponents/Textarea";
import { useFormContext } from "../../../context/FormContext";
import { useTableContext } from "../../../context/TableContext";
import { getToolTipAttributes } from "../../../utils/tooltip";
import { AppUser, Lookup, LookupSm, SliderBox } from "../FormFieldComponents";
import FormGridLayout from "../FormGridLayout";
import ButtonsGroup from "./ButtonsGroup";
import Radio from "./Radio";
import Select from "./Select";
import SelectBoxes from "./SelectBoxes";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import IconPreview from "../../../components/PreviewPositionedElement/IconPreview";
import RichGrid from "./RichGrid";

const FieldTag = ({ element, setEditFieldId, editFieldId, dragFromOutside, setDragFromOutside, activeMode, from }) => {
  const getLabelStyles = () => {
    if (element.type !== "label" || !element.style) return {};
    const className = Object.values(element.style.className || {})
      .filter(Boolean)
      .join(" ");
    const style = element.style.inline_style || {};
    return { className, style };
  };

  const labelStyles = getLabelStyles();

  const renderLookupTag = () => {
    const isLookupType = element.type === "lookup" || element.type === "lookup-sm";
    const displayField = element.display_fields?.[0]?.activity_label;

    if (isLookupType && displayField) {
      return <div className="key_tag">({displayField})</div>;
    }

    return null;
  };
  return (
    <>
      <div className="d_flex align_center gap_2 mb_2">
        <div className="d_flex space_between w_100">
          <div className="d_flex gap_3 justify_between w_100">
            <Label
              className={`builder_label ${element.type === "label" ? labelStyles.className : ""}`}
              style={element.type === "label" ? labelStyles.style : {}}
            >
              {element.label}
              {element.validate?.required && <span>*</span>}
            </Label>

            {renderLookupTag()}
            <FieldChips element={element} />
          </div>

          {element.pre_filters?.length > 0 && (
            <p className="d_flex gap_1 builder_label">
              Pre filters{" "}
              <span className="d_flex gap_1 justify_center align_center mb_1 pre_filter_tag">
                {element.pre_filters.length}
              </span>
            </p>
          )}
        </div>

        {element.help_text && <HelpText content={element.help_text} />}
      </div>

      <GetFieldTag
        element={element}
        setEditFieldId={setEditFieldId}
        editFieldId={editFieldId}
        dragFromOutside={dragFromOutside}
        setDragFromOutside={setDragFromOutside}
        activeMode={activeMode}
        from={from}
      />
    </>
  );
};

const chipStyle = {
  fontSize: "10px",
  padding: "2px 8px",
  borderRadius: "4px",
  marginBottom: "4px",
  marginLeft: "auto",
  fontWeight: "500",
  textTransform: "uppercase",
  border: "1px solid",
  display: "inline-block",
  backgroundColor: "#f5f5f5", // light gray
  color: "#555", // soft dark gray
  borderColor: "#d0d0d0" // subtle border
};

const FieldChips = ({ element }) => {
  if (!element || typeof element !== "object") return null;

  const showCustomJs = !!element?.validate?.custom_js?.trim();
  const showOnChange = Array.isArray(element?.on_change) && element.on_change.length > 0;

  const hasDefaultActionChildren =
    Array.isArray(element?.defaultAction?.action) &&
    element.defaultAction.action.some(
      (action) => action && Array.isArray(action.child_id) && action.child_id.length > 0
    );

  if (!showCustomJs && !showOnChange && !hasDefaultActionChildren) return null;

  return (
    <div className="d_flex gap_1">
      {hasDefaultActionChildren && <p style={chipStyle}>Action</p>}
      {showOnChange && <p style={chipStyle}>On-change</p>}
      {showCustomJs && <p style={chipStyle}>JS</p>}
    </div>
  );
};

const GetFieldTag = ({
  element,
  setEditFieldId,
  editFieldId,
  dragFromOutside,
  setDragFromOutside,
  activeMode,
  from
}) => {
  const { renderFieldsData } = useFormContext();

  switch (element?.type) {
    case "textfield":
    case "currency":
    case "disabled":
    case "entity":
      return <Input type="text" />;
    case "date":
    case "day":
    case "date-range":
      return <Input type="date" />;
    case "email":
      return <Input type="email" />;
    case "file":
      return <Input type="file" />;
    case "password":
      return <Input type="password" />;
    case "number":
      return <Input type="number" />;
    case "datetime":
      return <Input type="datetime-local" />;
    case "textarea":
      return <Textarea />;
    case "checkbox":
    case "toggle":
      return <Input type="checkbox" />;
    case "button":
    case "geolocation":
      return (
        <Button>
          {element.icon && <IconPreview config={{ icon_class: element.icon }} classes="mr_1" />}
          {element.label}
        </Button>
      );
    case "button-group":
      return <ButtonsGroup element={element} />;
    case "lookup":
      return <Lookup element={element} />;
    case "lookup-sm":
      return <LookupSm element={element} />;
    case "app-user":
      return <AppUser fields={element.display_fields} />;
    case "select":
    case "multi-select-dropdown":
      return <Select element={element} />;
    case "radio":
      return <Radio element={element} />;
    case "selectboxes":
    case "select-chips":
      return <SelectBoxes element={element} />;
    case "container":
    case "recordset-list":
    case "recordset":
      if (renderFieldsData.current[element.id]?.components) {
        return (
          <FormGridLayout
            setEditFieldId={setEditFieldId}
            editFieldId={editFieldId}
            dragFromOutside={dragFromOutside}
            setDragFromOutside={setDragFromOutside}
            fieldId={element.id}
            activeMode={activeMode}
            from={from}
          />
        );
      } else {
        return element.components.map((f) => <FieldTag element={f} key={f.id} />);
      }
    case "global-data":
      return (
        <RenderGlobalDataField
          element={element}
          activeMode={activeMode}
          setEditFieldId={setEditFieldId}
          editFieldId={editFieldId}
          dragFromOutside={dragFromOutside}
          setDragFromOutside={setDragFromOutside}
          from={from}
        />
      );
    case "label":
      return null;
    case "content":
      return <div dangerouslySetInnerHTML={{ __html: element.value }} />;
    case "slider":
      return (
        <div className="builder_form">
          <SliderBox element={element} />
        </div>
      );
    case "rich-grid":
      return <RichGrid element={element} />;
    default:
      return <h4>No Tag Found</h4>;
  }
};

const RenderGlobalDataField = ({
  element,
  activeMode,
  setEditFieldId,
  editFieldId,
  dragFromOutside,
  setDragFromOutside,
  from
}) => {
  const { getTableTypeData } = useTableContext();
  const { selectedForm } = useFormContext();
  const [globalData, setGlobalData] = useState(null);

  useEffect(() => {
    if (selectedForm && selectedForm.parentData) {
      const fetchTable = async () => {
        try {
          let { tableObject } = await getTableTypeData("statemachine", selectedForm.parentData.split("/")[0]);
          setGlobalData(tableObject);
        } catch (error) {
          console.error(error);
        }
      };

      fetchTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForm]);
  if (
    element.global_field.field_type &&
    element.global_field.field_type === "recordset-list" &&
    (activeMode === "desktop" || activeMode === "mobile")
  ) {
    return (
      <FormGridLayout
        setEditFieldId={setEditFieldId}
        editFieldId={editFieldId}
        dragFromOutside={dragFromOutside}
        setDragFromOutside={setDragFromOutside}
        from={from}
        fieldId={element.id}
        activeMode={activeMode}
      />
    );
  } else if (element.global_field.field_type && element.global_field.field_type === "rich-grid") {
    return <RichGrid element={element} />;
  }
  if (globalData) {
    return <div>{globalData[element.global_field.id]?.display || ""}</div>;
  }
  return <div>{element?.global_field?.display}</div>;
};

export default FieldTag;

const HelpText = ({ content }) => {
  return (
    <div style={{ userSelect: "text", height: "18px" }}>
      <div {...getToolTipAttributes(content)} className="cursor_pointer">
        <MaterialIcon name="help" className="font_size_medium font_weight_normal" />
      </div>
    </div>
  );
};
